import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ZoomIn, ZoomOut, Home, Settings, Save, AlertCircle, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { displaySourceFor, loadLotImage, LotImageLoadError, lotImageLoadErrorMessage } from '@/lib/parkingLot/lotImage';
import { ParkingLotTicketPanel } from './ParkingLotTicketPanel';
import { ParkingLotLayoutEditor } from './ParkingLotLayoutEditor';
import { usePermissions } from '@/hooks/usePermissions';

interface ParkingLotImage {
  id: string;
  image_path: string;
  image_width: number;
  image_height: number;
  status: string;
  scale_ft_per_pixel: number | null;
}

interface ParkingLotSpot {
  id: string;
  layout_id: string;
  label: string;
  spot_type: string;
  geom_px: number[][][]; // Pixel coordinates polygon
  is_active: boolean;
  ticketId?: string | null;
}

interface ParkingLotTicket {
  id: string;
  vehicleInfo: string;
  customer_name: string;
  status: string;
  parking_spot_id: string | null;
}

interface ParkingLotCanvasViewProps {
  shopId: string;
  image: ParkingLotImage;
  spots: ParkingLotSpot[];
  tickets: ParkingLotTicket[];
  onSpotsChange: (spots: ParkingLotSpot[]) => void;
  onSaveSpots: () => Promise<boolean>;
  onAssignTicket: (ticketId: string, spotId: string | null) => Promise<boolean>;
}

export function ParkingLotCanvasView({
  shopId,
  image,
  spots,
  tickets,
  onSpotsChange,
  onSaveSpots,
  onAssignTicket,
}: ParkingLotCanvasViewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  // Drawn at the natural size (downscaled copy when oversized) so spot polygons keep their pixel coordinates.
  const [displaySource, setDisplaySource] = useState<CanvasImageSource | null>(null);
  // NOV4 P2: a failed load is an actionable message with Retry, never an endless spinner.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  
  // View state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  
  // Selection state
  const [selectedSpotId, setSelectedSpotId] = useState<string | null>(null);
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const { isManager, isServiceWriter } = usePermissions();
  const canEdit = isManager || isServiceWriter;
  const { toast } = useToast();

  // Sign the stored path, then load the image — every failure ends in `loadError`.
  useEffect(() => {
    let cancelled = false;
    setImageLoaded(false);
    setLoadError(null);
    (async () => {
      try {
        const { data, error } = await supabase.storage
          .from('parking-lot-images')
          .createSignedUrl(image.image_path, 3600);
        if (error || !data?.signedUrl) throw new LotImageLoadError('signed_url', error?.message);
        const img = await loadLotImage(data.signedUrl);
        if (cancelled) return;
        setImageElement(img);
        setDisplaySource(displaySourceFor(img));
        setImageLoaded(true);

        // Calculate initial scale to fit container
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const containerHeight = containerRef.current.clientHeight;
          const imgAspect = img.naturalWidth / img.naturalHeight;
          const containerAspect = containerWidth / containerHeight;

          let initialScale: number;
          if (imgAspect > containerAspect) {
            initialScale = containerWidth / img.naturalWidth;
          } else {
            initialScale = containerHeight / img.naturalHeight;
          }
          setScale(initialScale * 0.95);

          // Center the image
          const scaledWidth = img.naturalWidth * initialScale * 0.95;
          const scaledHeight = img.naturalHeight * initialScale * 0.95;
          setOffset({
            x: (containerWidth - scaledWidth) / 2,
            y: (containerHeight - scaledHeight) / 2,
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load parking lot image:', err);
        setLoadError(err instanceof LotImageLoadError ? err.message : lotImageLoadErrorMessage('decode'));
      }
    })();
    return () => { cancelled = true; };
  }, [image.image_path, loadAttempt]);

  // Draw canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !imageElement || !displaySource) return;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw image
    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);
    ctx.drawImage(displaySource, 0, 0, imageElement.naturalWidth, imageElement.naturalHeight);
    ctx.restore();

    // Draw spots
    spots.forEach(spot => {
      if (!spot.geom_px || !spot.geom_px[0]) return;
      
      const coords = spot.geom_px[0];
      const isSelected = spot.id === selectedSpotId;
      const hasTicket = !!spot.ticketId;
      
      // Draw polygon
      ctx.beginPath();
      coords.forEach((coord, i) => {
        const screenX = coord[0] * scale + offset.x;
        const screenY = coord[1] * scale + offset.y;
        if (i === 0) {
          ctx.moveTo(screenX, screenY);
        } else {
          ctx.lineTo(screenX, screenY);
        }
      });
      ctx.closePath();
      
      // Fill color based on state
      if (isSelected) {
        ctx.fillStyle = 'rgba(251, 191, 36, 0.6)'; // Yellow
      } else if (hasTicket) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.6)'; // Blue
      } else {
        ctx.fillStyle = 'rgba(107, 114, 128, 0.6)'; // Gray
      }
      ctx.fill();
      
      // Stroke
      ctx.strokeStyle = isSelected ? '#f59e0b' : '#1f2937';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw label
      const centroid = getCentroid(coords);
      const labelX = centroid[0] * scale + offset.x;
      const labelY = centroid[1] * scale + offset.y;
      
      ctx.font = `bold ${14 * Math.max(1, scale)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 3;
      ctx.strokeText(spot.label, labelX, labelY);
      ctx.fillText(spot.label, labelX, labelY);
    });
  }, [imageElement, displaySource, scale, offset, spots, selectedSpotId]);

  // Get centroid of polygon
  const getCentroid = (coords: number[][]): [number, number] => {
    let x = 0, y = 0;
    coords.forEach(c => {
      x += c[0];
      y += c[1];
    });
    return [x / coords.length, y / coords.length];
  };

  // Redraw on changes
  useEffect(() => {
    draw();
  }, [draw]);

  // Handle canvas resize
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
      draw();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [draw]);

  // Handle canvas clicks
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    
    // Convert screen coords to image coords
    const imageX = (screenX - offset.x) / scale;
    const imageY = (screenY - offset.y) / scale;
    
    // Check if clicked inside any spot
    for (const spot of spots) {
      if (!spot.geom_px || !spot.geom_px[0]) continue;
      if (pointInPolygon([imageX, imageY], spot.geom_px[0])) {
        setSelectedSpotId(spot.id);
        return;
      }
    }
    
    setSelectedSpotId(null);
  };

  // Point in polygon test
  const pointInPolygon = (point: number[], polygon: number[][]): boolean => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];
      
      const intersect = ((yi > point[1]) !== (yj > point[1])) &&
        (point[0] < (xj - xi) * (point[1] - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || e.ctrlKey || e.button === 2) {
      e.preventDefault();
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const dx = e.clientX - lastPanPoint.x;
      const dy = e.clientY - lastPanPoint.y;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(5, scale * delta));
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const newOffsetX = mouseX - (mouseX - offset.x) * (newScale / scale);
      const newOffsetY = mouseY - (mouseY - offset.y) * (newScale / scale);
      
      setOffset({ x: newOffsetX, y: newOffsetY });
    }
    
    setScale(newScale);
  };

  const resetView = () => {
    if (!imageElement || !containerRef.current) return;
    
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const imgAspect = imageElement.naturalWidth / imageElement.naturalHeight;
    const containerAspect = containerWidth / containerHeight;
    
    let initialScale: number;
    if (imgAspect > containerAspect) {
      initialScale = containerWidth / imageElement.naturalWidth;
    } else {
      initialScale = containerHeight / imageElement.naturalHeight;
    }
    setScale(initialScale * 0.95);
    
    const scaledWidth = imageElement.naturalWidth * initialScale * 0.95;
    const scaledHeight = imageElement.naturalHeight * initialScale * 0.95;
    setOffset({
      x: (containerWidth - scaledWidth) / 2,
      y: (containerHeight - scaledHeight) / 2,
    });
  };

  // Handle ticket operations
  const handleTicketDrop = async (ticketId: string, spotId: string) => {
    await onAssignTicket(ticketId, spotId);
    setSelectedSpotId(null);
  };

  const handleUnassignTicket = async (ticketId: string) => {
    await onAssignTicket(ticketId, null);
    setSelectedSpotId(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveSpots();
    } finally {
      setIsSaving(false);
    }
  };

  // Map spots to include ticketIds
  const spotsWithTickets = spots.map(spot => ({
    ...spot,
    ticketId: tickets.find(t => t.parking_spot_id === spot.id)?.id || null,
  }));

  return (
    <div className="flex gap-4 h-[600px]">
      {/* Canvas area */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-2">
          {canEdit && (
            <>
              <Button
                variant={isEditorMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsEditorMode(!isEditorMode)}
                className="gap-2"
              >
                <Settings className="h-4 w-4" />
                {isEditorMode ? 'Exit Editor' : 'Edit Layout'}
              </Button>
              {isEditorMode && (
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Layout
                </Button>
              )}
            </>
          )}
          <div className="flex-1" />
          <Button variant="outline" size="icon" onClick={resetView}>
            <Home className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setScale(s => Math.min(5, s * 1.2))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setScale(s => Math.max(0.1, s / 1.2))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>

        {/* Canvas */}
        <div 
          ref={containerRef}
          className="flex-1 relative bg-muted rounded-lg overflow-hidden"
        >
          {loadError ? (
            <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="parking-lot-image-error">
              <div className="max-w-md text-center space-y-4">
                <AlertCircle className="h-8 w-8 mx-auto text-destructive" />
                <p className="text-sm">{loadError}</p>
                <Button variant="outline" onClick={() => setLoadAttempt((n) => n + 1)} data-testid="parking-lot-image-retry">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Try again
                </Button>
              </div>
            </div>
          ) : !imageLoaded ? (
            <div className="absolute inset-0 flex items-center justify-center" data-testid="parking-lot-image-loading">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <canvas
              ref={canvasRef}
              className={cn(
                'w-full h-full',
                isPanning ? 'cursor-grabbing' : 'cursor-pointer'
              )}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
        </div>
      </div>

      {/* Side panel */}
      {isEditorMode ? (
        <ParkingLotLayoutEditor
          spots={spots}
          onSpotsChange={onSpotsChange}
          imageWidth={image.image_width}
          imageHeight={image.image_height}
          scaleFtPerPixel={image.scale_ft_per_pixel || 0.1}
        />
      ) : (
        <ParkingLotTicketPanel
          tickets={tickets}
          spots={spotsWithTickets as any}
          selectedSpotId={selectedSpotId}
          onTicketDrop={handleTicketDrop}
          onUnassignTicket={handleUnassignTicket}
        />
      )}
    </div>
  );
}
